import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_643f3f34-a1d4-4c71-8acb-3c1d3f8beb8d/artifacts/bl51k3pb_95533D89-11D2-45C0-9D79-4F0FFEBEC907.png";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0a0a0a] border-t border-[#27272a] pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        {/* CTA block */}
        <div
          data-testid="footer-cta"
          className="border border-[#27272a] rounded-sm p-8 md:p-14 mb-20 relative overflow-hidden"
          style={{ background: "linear-gradient(180deg, #0f0f0f, #0a0a0a)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="eyebrow mb-6">
                <span className="gold-line" />
                Ready When You Are
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6">
                Ready to look established online?
              </h2>
              <p className="text-[#a1a1aa] text-base md:text-lg max-w-xl leading-relaxed">
                Agent Marketing helps agents and agencies build the brand,
                website, search presence, content, automation, and marketing
                systems needed to compete with confidence. You do not need to
                figure it all out alone.
              </p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Link
                to="/contact"
                data-testid="footer-cta-primary"
                className="btn-gold w-full md:w-auto"
              >
                Build My Growth Plan
              </Link>
              <Link
                to="/packages"
                data-testid="footer-cta-secondary"
                className="btn-outline w-full md:w-auto"
              >
                View Packages
              </Link>
            </div>
          </div>
        </div>

        {/* Main columns */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10 mb-16">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src={LOGO_URL}
                alt="Agent Marketing"
                className="h-12 w-12 object-contain"
              />
              <div>
                <div className="font-heading text-white uppercase tracking-widest text-sm">
                  Agent<span className="text-[#C8A24B] ml-1">Marketing</span>
                </div>
                <div className="text-[9px] tracking-[0.28em] text-[#71717a] uppercase mt-1">
                  Where Clients Find You.
                </div>
              </div>
            </Link>
            <p className="text-[#71717a] text-sm leading-relaxed max-w-xs">
              Presence systems, marketing infrastructure, and ongoing support
              for agents and agencies who depend on trust to win business.
            </p>
          </div>

          <FooterCol
            title="Explore"
            items={[
              { to: "/", label: "Home" },
              { to: "/packages", label: "Packages" },
              { to: "/pricing", label: "Pricing" },
              { to: "/growth-journey", label: "Growth Journey" },
              { to: "/work", label: "Work" },
            ]}
          />
          <FooterCol
            title="Services"
            items={[
              { to: "/services/branding", label: "Branding" },
              { to: "/services/websites", label: "Websites" },
              { to: "/services/google-business-profile", label: "Google Profile" },
              { to: "/services/listing-templates", label: "Listing Templates" },
              { to: "/services/ai-receptionist", label: "AI Reception" },
            ]}
          />
          <FooterCol
            title="Industries"
            items={[
              { to: "/industries/real-estate", label: "Real Estate" },
              { to: "/industries/mortgage", label: "Mortgage" },
              { to: "/industries/insurance", label: "Insurance" },
              { to: "/industries/recruiting", label: "Recruiting" },
              { to: "/industries/agencies", label: "Agencies" },
            ]}
          />
          <FooterCol
            title="Company"
            items={[
              { to: "/resources", label: "Resources" },
              { to: "/faq", label: "FAQ" },
              { to: "/contact", label: "Contact" },
              { to: "/privacy-policy", label: "Privacy Policy" },
              { to: "/terms", label: "Terms" },
            ]}
          />
        </div>

        <div className="rule-gold mb-10" />

        {/* Bottom band */}
        <div className="grid md:grid-cols-3 gap-6 items-start">
          <div>
            <p className="text-[#71717a] text-xs leading-relaxed max-w-sm">
              Agent Marketing is a branded division of{" "}
              <a
                href="https://kostaja.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a1a1aa] hover:text-[#C8A24B]"
              >
                Kostaja Ltd.
              </a>
            </p>
            <p className="text-[#52525b] text-xs leading-relaxed mt-2 max-w-sm">
              Serving agents and agencies across Canada, the United States,
              Australia, and England.
            </p>
          </div>
          <div className="text-[#52525b] text-xs md:text-center">
            &copy; {new Date().getFullYear()} Kostaja Ltd. All rights reserved.
          </div>
          <div className="md:text-right">
            <a
              href="https://kostaja.ca"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-made-by-kostaja"
              className="inline-flex items-center gap-2 text-xs text-[#a1a1aa] hover:text-white transition-colors"
            >
              <span className="text-[#52525b]">Made by</span>
              <span className="text-[#C8A24B] font-medium tracking-wider">
                Kostaja
              </span>
              <ArrowUpRight size={12} className="text-[#C8A24B]" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#71717a] mb-4 font-medium">
        {title}
      </div>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.to}>
            <Link
              to={it.to}
              className="text-[13px] text-[#d4d4d8] hover:text-[#C8A24B] transition-colors"
            >
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
